import mediator from '../utilities/mediator';
import log from '../utilities/log';

import { Study, StudyFull } from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { EntityState } from 'adc-sirius-module-common';
import { ApiErrorResponse } from '../models';

type StudiesQueryParams = {
  organizationKey?: string;
  state?: EntityState;
};

type StudyQueryParams = {
  key?: string;
};

type UpdateStudyVariables = {
  studyKey: Study['key'];
  updatedStudyValues: Partial<Study>;
};

type CreateStudyVariables = {
  study: Study;
};

export function studiesQueryOptions(
  params?: StudiesQueryParams,
  options?: Partial<UseQueryOptions<Study[]>>,
) {
  return queryOptions<Study[]>({
    ...options,
    queryKey: ['studies', params],
    queryFn: async () => {
      try {
        return await mediator.request<Study[]>('study:getStudies', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.organizationKey,
  });
}

export function studyQueryOptions(
  params: StudyQueryParams,
  options?: Partial<UseQueryOptions<Study>>,
) {
  return queryOptions<Study>({
    ...options,
    queryKey: ['study', params],
    queryFn: async () => {
      try {
        return await mediator.request<Study>('study:getStudyByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.key,
  });
}

export function updateStudyMutateOptions(
  options?: Partial<UseMutationOptions<Study, Error, UpdateStudyVariables>>,
) {
  return mutationOptions<Study, Error, UpdateStudyVariables>({
    ...options,
    mutationFn: async ({ updatedStudyValues }: UpdateStudyVariables) => {
      try {
        return await mediator.request<Study>('study:updateStudy', {
          data: updatedStudyValues,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function studyFullQueryOptions(
  params: StudyQueryParams,
  options?: Partial<UseQueryOptions<StudyFull>>,
) {
  return queryOptions<StudyFull>({
    ...options,
    queryKey: ['studyFull', params],
    queryFn: async () => {
      try {
        return await mediator.request<StudyFull>('study:getStudyFullByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.key,
  });
}

export function createStudyMutateOptions(
  options?: Partial<UseMutationOptions<Study, Error, CreateStudyVariables>>,
) {
  return mutationOptions<Study, Error, CreateStudyVariables>({
    ...options,
    mutationFn: async ({ study }: CreateStudyVariables) => {
      try {
        return await mediator.request<Study>('study:createStudy', {
          data: study,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
